<template>
  <MglMarker :coordinates="coordinates">
    <template slot="marker">
      <component :is="markerProps.is" v-bind="markerProps" />
    </template>
    <MglPopup :offset="size / 2 + 12" :closeButton="false" @open="onOpen">
      <AggregateHospitalPopup :dataset="dataset" :resource="resource" />
    </MglPopup>
  </MglMarker>
</template>

<script>
import AggregateHospitalPopup from '@/components/HospitalMap/aggregate-hospital-popup';
import DonutChart from '@/components/donut-chart';
import StatusDot from '@/components/status-dot';
import centerMapOnPopup from '@/util/centerMapOnPopup';
import get from 'lodash/get';
import { AggregateChartColors, AggregateGranularity } from '@/store/types';
import { MglMarker, MglPopup } from 'vue-mapbox';

const Size = Object.freeze({
  XS: 16,
  SM: 26,
  MD: 36,
  LG: 44,
});

export default {
  components: { AggregateHospitalPopup, DonutChart, MglMarker, MglPopup },
  props: {
    dataset: {
      type: Object,
      required: true,
    },
    resource: {
      type: String,
      required: true,
    },
  },
  computed: {
    chartData() {
      // return default of 0, handle instances where the value is <= -1
      const getValue = key =>
        Math.max(get(this.dataset, [this.resource, key], 0), 0);
      return Object.entries(AggregateChartColors).map(([label, color]) => ({
        color,
        value: getValue(label.toLowerCase()),
      }));
    },
    coordinates() {
      return [this.dataset.location.lng, this.dataset.location.lat];
    },
    markerProps() {
      const hasData = !!this.chartData.filter(
        ({ color, value }) => color !== AggregateChartColors.NO_DATA && value,
      ).length;
      return hasData
        ? {
            dataset: this.chartData,
            is: DonutChart,
            size: this.size,
          }
        : {
            is: StatusDot,
            size: 12,
            strokeWidth: 2,
          };
    },
    size() {
      // Determine a size for the marker based on the number of hospitals the
      // marker represents at different granularity levels.
      // TODO(jjandoc): The breakpoints here are fairly arbitrary. There's
      //   probably a more clever or data-informed way to determine groupings.
      const hospitals = get(this, 'dataset.total_hospitals', 0);
      switch (this.dataset.level) {
        case AggregateGranularity.STATE:
          if (hospitals > 200) {
            return Size.LG;
          }
          if (hospitals > 100) {
            return Size.MD;
          }
          if (hospitals > 10) {
            return Size.SM;
          }
          return Size.XS;
        case AggregateGranularity.COUNTY:
          if (hospitals > 20) {
            return Size.LG;
          }
          if (hospitals > 10) {
            return Size.MD;
          }
          return Size.SM;
        case AggregateGranularity.CITY:
          if (hospitals > 10) {
            return Size.LG;
          }
          if (hospitals > 5) {
            return Size.MD;
          }
          return Size.SM;
        default:
          return Size.MD;
      }
    },
  },
  methods: {
    onOpen(event) {
      const { map, popup } = event;
      centerMapOnPopup(map, popup);
    },
  },
};
</script>
